import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import {
  Heading,
  Skeleton,
  Flex,
  Text,
  MenuButton,
  Button,
  Menu,
  MenuItem,
  MenuList,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Link,
} from '@chakra-ui/react'
import { FaChevronDown } from 'react-icons/fa'
import { useNavigate, useParams } from 'react-router-dom'

import { Page, Status, ManualDeployModal } from '../../components'
import Deployments from './tabs/Deployments'
import Logs from './tabs/Logs'
import Environment from './tabs/Environment'
// import Resources from './tabs/Resources'
import Settings from './tabs/Settings'

const APPLICATION_QUERY = gql`
  query Application($appName: String!) {
    app(name: $appName) {
      id
      displayName
      defaultBranch
      status
      hostname
    }
  }
`

type TabType = { name: string; displayName: string; component: ReactNode }

const TABS: TabType[] = [
  {
    name: 'deployments',
    displayName: 'Deployments',
    component: <Deployments />,
  },
  {
    name: 'logs',
    displayName: 'Logs',
    component: <Logs />,
  },
  {
    name: 'environment',
    displayName: 'Environment',
    component: <Environment />,
  },
  // {
  //   name: 'resources',
  //   displayName: 'Resources',
  //   component: <Resources />,
  // },
  {
    name: 'settings',
    displayName: 'Settings',
    component: <Settings />,
  },
]

const TAB_MAP = TABS.reduce<Record<string, TabType>>((tabs, tab, i) => {
  tabs[i] = tab
  return tabs
}, {})

const Application = () => {
  const { name, tab } = useParams()
  const navigate = useNavigate()

  const [isManualDeployModalOpen, setIsManualDeployModalOpen] = useState(false)

  const { data, loading, error } = useQuery(APPLICATION_QUERY, {
    variables: { appName: name },
    pollInterval: 10 * 1000,
  })

  const isLoaded = !!(data && !loading)

  useEffect(() => {
    if (tab === undefined) {
      navigate(`/app/${name}/${TAB_MAP['0'].name}`)
    }
  }, [name, tab, navigate])

  const tabIndex = useMemo(() => {
    const [index] =
      Object.entries(TAB_MAP).find(([_, values]) => values.name === tab) ??
      Object.entries(TAB_MAP)[0]

    return parseInt(index)
  }, [tab])

  const handleTabChange = useCallback(
    (index: number) => {
      const tabName = TAB_MAP[`${index}`].name

      navigate(`/app/${name}/${tabName}`)
    },
    [name, navigate]
  )

  if (error) {
    return (
      <Page>
        <Heading>Ohh uhh...</Heading>
        <Text>
          An error occurred upon loading the data for your app: {name}
        </Text>
      </Page>
    )
  }

  const app = data?.app ?? {}

  const link = app?.hostname ? `http://${app.hostname}` : ''

  return (
    <Page>
      <Skeleton isLoaded={isLoaded}>
        <Flex alignItems="center">
          <Heading>{app?.displayName}</Heading>
          <Status ml={12} status={app?.status ?? ''} size="lg" />
          <Spacer />
          {link ? <Link mr={12} target="_blank" rel="noreferrer" href={link}>Preview app</Link> : null}
          <Menu>
            <MenuButton as={Button} rightIcon={<FaChevronDown />}>
              Actions
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => setIsManualDeployModalOpen(true)}>
                Manual deploy
              </MenuItem>
              <MenuItem isDisabled>Rollback to previous commit</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Skeleton>
      <Tabs
        isLazy
        index={tabIndex}
        onChange={handleTabChange}
        colorScheme="cyan"
        mt={6}
      >
        <TabList>
          {Object.values(TAB_MAP).map((tab) => (
            <Tab key={tab.name}>{tab.displayName}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {Object.values(TAB_MAP).map((tab) => (
            <TabPanel key={tab.name} px="0" pt="8">
              {tab.component}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      {name && app.defaultBranch && (
        <ManualDeployModal
          isOpen={isManualDeployModalOpen}
          onClose={() => setIsManualDeployModalOpen(false)}
          appName={name}
          defaultBranch={app.defaultBranch}
        />
      )}
    </Page>
  )
}

export default Application
